import React, { createContext, useContext, useState } from "react";
import { Constants } from "../../../constants";
import Helpers from "../../../constants/helpers";
import { SubscriptionType, UpsellPanelSteps, UserAccessLevelType } from "../../../constants/upsellPanelEnumsAndConstants";
import { isEurekaDomain, isIntelligentInvestorDomain } from "../../../utils/utils";
import { useUpsellWidgetModel } from "./useUpsellWidgetModelContext";

const UpsellWidgetContainerContext = createContext(null);

const useBackgroundClass = () => {
    if (isEurekaDomain()) return "eureka";
    if (isIntelligentInvestorDomain()) return "ii";
    return "";
};
const useHeaderFontClass = () => {
    return isEurekaDomain() ? "font-19" : isIntelligentInvestorDomain() ? "font-30" : "font-25";
};
const useBoldClass = () => {
    return isIntelligentInvestorDomain() ? "font-weight-semibold" : "font-circular-std-bold";
};
const getIsPaymentRequired = (productHandle, card) => {
    if (productHandle == SubscriptionType.SUBSCRIPTION_TRIAL_PRODUCT
        || productHandle == SubscriptionType.SUBSCRIPTION_INSIGHTS_FREE_PRODUCT_HANDLE)
        return false;
    if (card?.PriceAfterCoupon == 0)
        return false;

    return true;
}

export const UpsellWidgetContainerProvider = ({
    children,
    currentStep,
    goToStep,
    onCloseBtnClick,
    baseProps,
    setBaseProps
}) => {
    const [isUpsellContainerLoading, setIsUpsellContainerLoading] = useState(false);
    const model = useUpsellWidgetModel();
    const bgCssClass = useBackgroundClass();
    const boldClass = useBoldClass();
    const headerFontClass = useHeaderFontClass();
    const updateBaseProps = (newState) => {
        setBaseProps((prevState) => ({
            ...prevState,
            ...newState
        }))
    }

    const handleActivateTrialSuccess = (response, redirectToUrl, setFormErrors) => {
        if (response.status === Constants.RequestStatus.SUCCESS) {
            window.authenticateAllDomains(response);
            redirectToUrl('', model.targetUrl ?? model.downloadFileUrl ?? window.location.href, 1500);
            goToStep(UpsellPanelSteps.Step6Final);
        } else if (response.errors) {
            handleErrorResponse(response.errors, setFormErrors);
        }
        setIsUpsellContainerLoading(false);
    }

    const handleProductActivation = (response, redirectToUrl, model, activateTrial, onActivateTrial) => {
        if (model.isEurekaFund) {
            const message = `Thank you ${response.FirstName}. We have successfully created your account.`;
            goToStep(UpsellPanelSteps.Step6Final);
            redirectToUrl(message, `/invest-with-us/addtowaitinglist/${ModelPortfolioType.EurekaFund}`);
        } else if (model.productHandle == SubscriptionType.SUBSCRIPTION_TRIAL_PRODUCT || activateTrial || model.showPricingPanel) {
            if ((model.byPassPhoneVerification || response.userIsPhoneVerified) &&
                (model.currentStep == 0
                    || model.currentStep == UpsellPanelSteps.Step15PopupThankYou
                    || currentStep == UpsellPanelSteps.Step18PricingPanel)
            ) {
                goToStep(UpsellPanelSteps.Step9PreProductActivation);
            } else {
                onActivateTrial();
            }
        } else if (model.productHandle == SubscriptionType.SUBSCRIPTION_INSIGHTS_FREE_PRODUCT_HANDLE) {
            if (model.usePopupOverlay) {
                if (model.requiredAccessLevel >= UserAccessLevelType.Essentials) {
                    goToStep(UpsellPanelSteps.Step4MobileUpdate);
                } else {
                    goToStep(UpsellPanelSteps.Step15PopupThankYou);
                }
            } else {
                var msg = "Thank you " + response.FirstName + ". We have successfully created your account.";
                redirectToUrl(msg, model.targetUrl2, 1500);
                goToStep(UpsellPanelSteps.Step6Final);
            }
        } else if (response.subscriptionStatus == Constants.CHARGIFY_SUBSCRIPTION_PAST_DUE_STATUS) {
            if (response.subscriptionPaymentUpdated === 'true')
                goToStep(UpsellPanelSteps.Step11PastDueStatusPaymentDetailsUpdated);
            else
                goToStep(UpsellPanelSteps.Step10PastDueStatusPaymentFailed);
        } else if (getIsPaymentRequired(model.productHandle, model.card)) {
            goToStep(UpsellPanelSteps.Step7PaymentForm);
        } else if (currentStep == UpsellPanelSteps.Step9PreProductActivation) {
            upgradeUser();
        } else if (!activateTrial && currentStep == UpsellPanelSteps.Step18PricingPanel) {
            goToStep(UpsellPanelSteps.Step18PricingPanel);
        } else {
            goToStep(UpsellPanelSteps.Step9PreProductActivation);
        }
    };
    const handleErrorResponse = (errors, setFormErrors) => {
        const transformedErrors = errors.map(error => ({
            field: Helpers.lowercaseFirstLetter(error.key),
            message: error.value,
        }));
        setFormErrors && setFormErrors(transformedErrors);
    }
    const onSubmitError = (error) => {
        setIsUpsellContainerLoading(false);
        console.log(error || "An error has occured, please try again");
    }
    const contextValue = {
        isUpsellContainerLoading,
        setIsUpsellContainerLoading,
        currentStep,
        goToStep,
        onCloseBtnClick,
        baseProps,
        updateBaseProps,
        bgCssClass,
        boldClass,
        headerFontClass,
        handleErrorResponse,
        onSubmitError,
        handleProductActivation,
        handleActivateTrialSuccess
    };

    return (
        <UpsellWidgetContainerContext.Provider value={contextValue}>
            {children}
        </UpsellWidgetContainerContext.Provider>
    );
};

export const useUpsellWidgetContainer = () => useContext(UpsellWidgetContainerContext);
